import type {
  FulfillmentCarrier,
  FulfillmentStatus,
  GoodsIssueStatus,
  GoodsReceiptStatus,
  HubTransferSide,
  HubTransferStatus,
  OutboundMethod,
  OutboundOrderSource,
  OutboundOrderStatus,
  OutboundOrderType,
  OutboundServiceType,
  PurchaseOrder,
  PurchaseOrderReceiptStatus,
  PurchaseOrderStatus,
  PurchaseOrderType,
  StorageCategory,
  StockOpnameStatus,
  StockOpnameType,
  StorageType,
  TransferStatus,
  VendorStatus,
  WarehouseStockType,
} from './types';

/** Kit Badge status scale (src/design-system/badge.tsx). */
export type KitTone =
  | 'error'
  | 'warning'
  | 'away'
  | 'success'
  | 'information'
  | 'feature'
  | 'verified'
  | 'highlighted'
  | 'stable'
  | 'faded'
  | 'neutral';

export interface StatusMeta {
  label: string;
  tone: KitTone;
}

export const OUTBOUND_STATUS: Record<OutboundOrderStatus, StatusMeta> = {
  OPEN: { label: 'Open', tone: 'information' },
  PREPARING: { label: 'Preparing', tone: 'away' },
  READY_TO_PICKUP: { label: 'Ready to pick up', tone: 'verified' },
  SHIPPED: { label: 'Shipped', tone: 'feature' },
  IN_DELIVERY: { label: 'In delivery', tone: 'away' },
  DELIVERED: { label: 'Delivered', tone: 'success' },
  FAILED: { label: 'Failed', tone: 'error' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

export const OUTBOUND_TABS: ('ALL' | OutboundOrderStatus)[] = [
  'ALL',
  'OPEN',
  'PREPARING',
  'READY_TO_PICKUP',
  'SHIPPED',
  'IN_DELIVERY',
  'DELIVERED',
  'FAILED',
  'CANCELLED',
];

export const OUTBOUND_CANCELLABLE: OutboundOrderStatus[] = [
  'OPEN',
  'PREPARING',
  'READY_TO_PICKUP',
];

/**
 * "Batal submit shipping" is open only while the goods are still at the hub:
 * the goods issue and its order are SHIPPED, the fulfillment has not been
 * dispatched (AT_HUB) and it is not a direct handover (already delivered).
 */
export const canUndoShipping = (s: {
  goodsIssueStatus?: GoodsIssueStatus | null;
  orderStatus?: OutboundOrderStatus | null;
  fulfillmentStatus?: FulfillmentStatus | null;
  carrier?: FulfillmentCarrier | null;
}): boolean =>
  s.goodsIssueStatus === 'SHIPPED' &&
  s.orderStatus === 'SHIPPED' &&
  s.fulfillmentStatus === 'AT_HUB' &&
  s.carrier !== 'HANDOVER';

/**
 * Cancel after submit shipping — while the goods are still at the hub (not
 * dispatched, not a handover): the shipment is cancelled and the shipped
 * stock goes back on hand (wms-outbound-order-cr1 § B). Same gate as
 * "Batal submit shipping", seen from the order.
 */
export const canCancelAfterShip = (o: {
  status: OutboundOrderStatus;
  fulfillment?: {
    status?: FulfillmentStatus | null;
    carrier?: FulfillmentCarrier | null;
  } | null;
}): boolean =>
  o.status === 'SHIPPED' &&
  o.fulfillment?.status === 'AT_HUB' &&
  o.fulfillment?.carrier !== 'HANDOVER';

/**
 * "Ubah" (recipient + destination) until In delivery. A shipped order on a
 * Dash shipment only while that shipment is still at the hub (the server
 * re-checks the shipment itself); a manual carrier until ops mark it In
 * delivery. A direct handover is DELIVERED at once.
 */
export const canEditOutbound = (o: {
  status: OutboundOrderStatus;
  fulfillment?: {
    status?: FulfillmentStatus | null;
    carrier?: FulfillmentCarrier | null;
  } | null;
}): boolean => {
  if (OUTBOUND_CANCELLABLE.includes(o.status)) return true;
  if (o.status !== 'SHIPPED') return false;
  const f = o.fulfillment;
  if (f?.carrier === 'HANDOVER') return false;
  return f?.carrier !== 'DASH' || f.status === 'AT_HUB';
};

export const SOURCE_META: Record<
  OutboundOrderSource,
  { label: string; tag: string; icon: string }
> = {
  MANUAL: { label: 'Manual', tag: 'Manual', icon: 'ri-edit-line' },
  CSV: { label: 'CSV upload', tag: 'CSV', icon: 'ri-upload-2-line' },
  CLIENT_API: {
    label: 'Client API',
    tag: 'Client API',
    icon: 'ri-code-s-slash-line',
  },
};

export const GOODS_ISSUE_STATUS: Record<GoodsIssueStatus, StatusMeta> = {
  RESERVED: { label: 'Not picked', tone: 'neutral' },
  PICKED: { label: 'Picked', tone: 'success' },
  READY_TO_PICKUP: { label: 'Ready to pick up', tone: 'verified' },
  SHIPPED: { label: 'Shipped', tone: 'feature' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

/** Picking list tabs: all, then the GI status each one narrows to. */
export const PICKING_TABS = ['ALL', 'RESERVED', 'PICKED'] as const;

/** Shipping view status = GI status until SHIPPED, then the order's. */
export const SHIPPING_STATUS: Record<string, StatusMeta> = {
  READY_TO_PICKUP: { label: 'Ready to pick up', tone: 'verified' },
  SHIPPED: { label: 'Shipped', tone: 'feature' },
  IN_DELIVERY: { label: 'In delivery', tone: 'away' },
  DELIVERED: { label: 'Delivered', tone: 'success' },
  FAILED: { label: 'Failed', tone: 'error' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

export const SHIPPING_TABS = [
  'ALL',
  'READY_TO_PICKUP',
  'SHIPPED',
  'IN_DELIVERY',
  'DELIVERED',
  'FAILED',
] as const;

export const FULFILLMENT_STATUS: Record<FulfillmentStatus, StatusMeta> = {
  AT_HUB: { label: 'At hub', tone: 'verified' },
  DISPATCHED: { label: 'Dispatched', tone: 'feature' },
  IN_DELIVERY: { label: 'In delivery', tone: 'away' },
  DELIVERED: { label: 'Delivered', tone: 'success' },
  FAILED: { label: 'Failed', tone: 'error' },
  RETURNED: { label: 'Returned', tone: 'error' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

export const PO_STATUS: Record<PurchaseOrderStatus, StatusMeta> = {
  DRAFT: { label: 'Draft', tone: 'neutral' },
  CONFIRMED: { label: 'Confirmed', tone: 'success' },
  CANCELLED: { label: 'Cancelled', tone: 'faded' },
};

export const GOODS_RECEIPT_STATUS: Record<GoodsReceiptStatus, StatusMeta> = {
  DRAFT: { label: 'Draft', tone: 'neutral' },
  POSTED: { label: 'Posted', tone: 'success' },
};

export const PO_TABS: ('ALL' | PurchaseOrderStatus)[] = [
  'ALL',
  'DRAFT',
  'CONFIRMED',
  'CANCELLED',
];

export const RECEIPT_STATUS: Record<PurchaseOrderReceiptStatus, StatusMeta> = {
  SCHEDULED: { label: 'Scheduled', tone: 'information' },
  PARTIAL_RECEIPT: { label: 'Partial receipt', tone: 'away' },
  FINISHED: { label: 'Finished', tone: 'success' },
};

export const RECEIPT_TABS: ('ALL' | PurchaseOrderReceiptStatus)[] = [
  'ALL',
  'SCHEDULED',
  'PARTIAL_RECEIPT',
  'FINISHED',
];

export const PO_TYPES: PurchaseOrderType[] = ['REGULAR', 'TRANSFER', 'RETURN'];

export const PO_TYPE_META: Record<
  PurchaseOrderType,
  { label: string; icon: string }
> = {
  REGULAR: { label: 'Regular', icon: 'ri-store-2-line' },
  TRANSFER: { label: 'Transfer', icon: 'ri-arrow-left-right-line' },
  RETURN: { label: 'Return', icon: 'ri-arrow-go-back-line' },
};

/** Rows from an older backend carry no `type` — they are vendor POs. */
export const poTypeOf = (po: Pick<PurchaseOrder, 'type'>): PurchaseOrderType =>
  po.type ?? 'REGULAR';

export interface PoSource {
  /** "Vendor" / "Hub asal" / "Diterima dari". */
  label: string;
  value: string;
  /** Vendor code or source hub code. */
  sub: string | null;
}

/** Where a PO's goods come from, labelled by type (detail, lists, print). */
export const poSourceOf = (
  po: Pick<PurchaseOrder, 'type' | 'vendor' | 'sourceHub' | 'supplierName'>
): PoSource => {
  switch (poTypeOf(po)) {
    case 'TRANSFER':
      return {
        label: 'Hub asal',
        value: po.sourceHub?.name || po.supplierName,
        sub: po.sourceHub?.code ?? null,
      };
    case 'RETURN':
      return { label: 'Diterima dari', value: po.supplierName, sub: null };
    default:
      return {
        label: 'Vendor',
        value: po.vendor?.name || po.supplierName,
        sub: po.vendor?.code ?? null,
      };
  }
};

/** Outbound service level (backend enum: SAME_DAY / NEXT_DAY). */
export const OUTBOUND_SERVICE_LABEL: Record<OutboundServiceType, string> = {
  SAME_DAY: 'Same day',
  NEXT_DAY: 'Next day',
};

/** Outbound method (backend enum: DELIVERY; TAKE_AWAY tolerated). */
export const OUTBOUND_METHOD_LABEL: Record<
  OutboundMethod | 'TAKE_AWAY',
  string
> = {
  DELIVERY: 'Delivery',
  TAKE_AWAY: 'Take away',
};

const enumLabel = (
  map: Record<string, string>,
  key: string | null | undefined
): string => (key ? (map[key] ?? key) : '—');

/** Label of an outbound service enum value; unknown values pass through. */
/** Who carries the goods out (Submit shipping). */
export const FULFILLMENT_CARRIER_LABEL: Record<FulfillmentCarrier, string> = {
  DASH: 'Dash',
  LION_PARCEL: 'Lion Parcel',
  HANDOVER: 'Serah terima langsung',
};
export const carrierLabel = (
  carrier: FulfillmentCarrier | null | undefined
): string | null => (carrier ? FULFILLMENT_CARRIER_LABEL[carrier] : null);

export const serviceLabel = (service: string | null | undefined): string =>
  enumLabel(OUTBOUND_SERVICE_LABEL, service);

/** Label of an outbound method enum value; unknown values pass through. */
export const methodLabel = (method: string | null | undefined): string =>
  enumLabel(OUTBOUND_METHOD_LABEL, method);

export const OUTBOUND_TYPES: OutboundOrderType[] = ['REGULAR', 'TRANSFER'];

export const OUTBOUND_TYPE_META: Record<
  OutboundOrderType,
  { label: string; icon: string }
> = {
  REGULAR: { label: 'Regular', icon: 'ri-truck-line' },
  TRANSFER: { label: 'Transfer', icon: 'ri-arrow-left-right-line' },
};

export const VENDOR_STATUS: Record<VendorStatus, StatusMeta> = {
  ACTIVE: { label: 'Active', tone: 'success' },
  INACTIVE: { label: 'Inactive', tone: 'faded' },
};

export const VENDOR_TABS = ['ALL', 'ACTIVE', 'INACTIVE'] as const;

export const STOCK_TYPES: WarehouseStockType[] = ['REGULAR', 'RETURN'];

/** Return stock is the one to look at: it came back from a customer, not from a vendor. */
export const STOCK_TYPE_META: Record<WarehouseStockType, StatusMeta> = {
  REGULAR: { label: 'Regular', tone: 'neutral' },
  RETURN: { label: 'Return', tone: 'warning' },
};

export const STORAGE_CATEGORY_LABEL: Record<StorageCategory, string> = {
  LOCATION: 'Location',
  SECTION: 'Section',
  BIN: 'Bin',
};

export const STORAGE_TYPE_LABEL: Record<StorageType, string> = {
  STORAGE: 'Storage',
  QUARANTINE: 'Quarantine',
  STAGING: 'Staging',
};

export const metaOf = (
  map: Record<string, StatusMeta>,
  key: string | null | undefined
): StatusMeta => (key && map[key]) || { label: key || '—', tone: 'neutral' };

/** GI statuses that still belong to the Picking screen. */
export const isPickingStage = (status: GoodsIssueStatus): boolean =>
  status === 'RESERVED' || status === 'PICKED';

/* transfer storage */

export const TRANSFER_STATUS: Record<TransferStatus, StatusMeta> = {
  REQUESTED: { label: 'Diajukan', tone: 'information' },
  APPROVED: { label: 'Disetujui', tone: 'verified' },
  PREPARING: { label: 'Disiapkan', tone: 'away' },
  DONE: { label: 'Selesai', tone: 'success' },
  REJECTED: { label: 'Ditolak', tone: 'error' },
  CANCELLED: { label: 'Dibatalkan', tone: 'faded' },
};

/** Transfer list tabs: all, then the status each one narrows to. */
export const TRANSFER_TABS = [
  'ALL',
  'REQUESTED',
  'APPROVED',
  'PREPARING',
  'DONE',
  'REJECTED',
  'CANCELLED',
] as const;

/* transfer warehouse (hub → hub) */

export const HUB_TRANSFER_STATUS: Record<HubTransferStatus, StatusMeta> = {
  REQUESTED: { label: 'Diajukan', tone: 'information' },
  FIRST_APPROVED: { label: 'Disetujui hub asal', tone: 'feature' },
  APPROVED: { label: 'Disetujui', tone: 'verified' },
  PREPARING: { label: 'Disiapkan', tone: 'away' },
  READY_TO_PICKUP: { label: 'Siap diambil', tone: 'verified' },
  SHIPPED: { label: 'Dikirim', tone: 'information' },
  PARTIAL_RECEIPT: { label: 'Diterima sebagian', tone: 'warning' },
  RECEIVED: { label: 'Diterima', tone: 'success' },
  REJECTED: { label: 'Ditolak', tone: 'error' },
  CANCELLED: { label: 'Dibatalkan', tone: 'faded' },
};

/**
 * List tabs: all, then every effective status on its own — the API's `status`
 * filter takes one value, and the counts (`/hub-transfers/summary`) are per
 * status, so a tab and its badge are always the same set.
 */
export const HUB_TRANSFER_TABS = [
  'ALL',
  'REQUESTED',
  'FIRST_APPROVED',
  'APPROVED',
  'PREPARING',
  'READY_TO_PICKUP',
  'SHIPPED',
  'PARTIAL_RECEIPT',
  'RECEIVED',
  'REJECTED',
  'CANCELLED',
] as const;

/** "Hub ini sebagai" toggle of the list: which end of the transfer the hub is. */
export const HUB_TRANSFER_SIDES = ['ANY', 'SOURCE', 'DESTINATION'] as const;

export const HUB_TRANSFER_SIDE_LABEL: Record<HubTransferSide, string> = {
  ANY: 'Semua',
  SOURCE: 'Asal',
  DESTINATION: 'Tujuan',
};

/** What the transfer waits for (null once nobody has to act on it here). */
export const hubTransferNextStep = (
  status: HubTransferStatus,
  orderCode?: string | null
): string | null => {
  switch (status) {
    case 'REQUESTED':
      return 'Menunggu approval hub asal';
    case 'FIRST_APPROVED':
      return 'Menunggu approval hub tujuan';
    case 'APPROVED':
      return orderCode
        ? `Menunggu picking order dibuat di outbound order ${orderCode}`
        : 'Menunggu picking order dibuat di outbound order';
    default:
      return null;
  }
};

/* stock opname */

export const OPNAME_STATUS: Record<StockOpnameStatus, StatusMeta> = {
  WAITING_MANAGER_APPROVAL: { label: 'Menunggu manager', tone: 'information' },
  WAITING_FAT_APPROVAL: { label: 'Menunggu FAT', tone: 'away' },
  DONE: { label: 'Selesai', tone: 'success' },
  REJECTED_BY_MANAGER: { label: 'Ditolak manager', tone: 'error' },
  REJECTED_BY_FAT: { label: 'Ditolak FAT', tone: 'error' },
  CANCELLED: { label: 'Dibatalkan', tone: 'faded' },
};

export const OPNAME_TYPE_META: Record<StockOpnameType, StatusMeta> = {
  WITH_ADJUSTMENT: { label: 'Dengan penyesuaian', tone: 'away' },
  WITHOUT_ADJUSTMENT: { label: 'Tanpa penyesuaian', tone: 'neutral' },
};

/** Opname list tabs; "Ditolak" covers both rejected statuses. */
export const OPNAME_TABS = [
  'ALL',
  'WAITING_MANAGER_APPROVAL',
  'WAITING_FAT_APPROVAL',
  'DONE',
  'REJECTED',
  'CANCELLED',
] as const;

export type OpnameTab = (typeof OPNAME_TABS)[number];

export const OPNAME_TAB_LABEL: Record<OpnameTab, string> = {
  ALL: 'Semua',
  WAITING_MANAGER_APPROVAL: 'Menunggu manager',
  WAITING_FAT_APPROVAL: 'Menunggu FAT',
  DONE: 'Selesai',
  REJECTED: 'Ditolak',
  CANCELLED: 'Dibatalkan',
};

/** The statuses one tab narrows to (null = every status). */
export const OPNAME_TAB_STATUSES: Record<
  OpnameTab,
  StockOpnameStatus[] | null
> = {
  ALL: null,
  WAITING_MANAGER_APPROVAL: ['WAITING_MANAGER_APPROVAL'],
  WAITING_FAT_APPROVAL: ['WAITING_FAT_APPROVAL'],
  DONE: ['DONE'],
  REJECTED: ['REJECTED_BY_MANAGER', 'REJECTED_BY_FAT'],
  CANCELLED: ['CANCELLED'],
};
